/**
 * Oath Agency Vitals Service
 * API calls for agency vitals/dashboard metrics using Oath authentication
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - extracts data from various response formats
 */
const parseResponse = async (response) => {
  const json = await response.json().catch(() => ({}));
  return json;
};

// ============================================
// AGENCY VITALS ENDPOINTS
// ============================================

/**
 * Get cached vitals for a specific agency
 * @param {number} agencyId - Agency ID
 * @returns {Promise<{success: boolean, data?: AgencyVitalsDto, cached?: boolean, error?: string}>}
 */
export const getAgencyVitals = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/vitals`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agency vitals' };
    }

    return {
      success: result.success !== false,
      data: result.data,
      cached: result.cached,
    };
  } catch (error) {
    console.error('Get agency vitals error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Force refresh vitals from database for a specific agency
 * @param {number} agencyId - Agency ID
 * @returns {Promise<{success: boolean, data?: AgencyVitalsDto, refreshed?: boolean, error?: string}>}
 */
export const refreshAgencyVitals = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/vitals/refresh`, {
      method: 'POST',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to refresh agency vitals' };
    }

    return {
      success: result.success !== false,
      data: result.data,
      refreshed: result.refreshed,
    };
  } catch (error) {
    console.error('Refresh agency vitals error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get vitals for all user's agencies
 * @returns {Promise<{success: boolean, data?: AgencyVitalsDto[], error?: string}>}
 */
export const getAllAgencyVitals = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/vitals/all`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get all agency vitals', data: [] };
    }

    return {
      success: result.success !== false,
      data: result.data || [],
    };
  } catch (error) {
    console.error('Get all agency vitals error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get aggregated totals across all user's agencies
 * @returns {Promise<{success: boolean, data?: VitalsSummary, error?: string}>}
 */
export const getAgencyVitalsSummary = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/vitals/summary`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get vitals summary' };
    }

    return {
      success: result.success !== false,
      data: result.data,
    };
  } catch (error) {
    console.error('Get agency vitals summary error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// CONSOLIDATED VITALS ENDPOINTS (General Agent)
// ============================================

/**
 * Get consolidated vitals across all sub-agencies for a general agent
 * @param {number} agencyId - The general agent (parent) agency ID
 * @returns {Promise<{success: boolean, data?: AgencyVitalsDto, cached?: boolean, error?: string}>}
 */
export const getConsolidatedVitals = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/vitals/consolidated`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get consolidated vitals' };
    }

    return {
      success: result.success !== false,
      data: result.data,
      cached: result.cached,
    };
  } catch (error) {
    console.error('Get consolidated vitals error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// SUB-AGENCY AR ENDPOINTS
// ============================================

/**
 * Get AR aging data for all sub-agencies under a general agent
 * @param {number} agencyId - The general agent (parent) agency ID
 * @returns {Promise<{success: boolean, data?: {generalAgentId: number, isGeneralAgent: boolean, subAgencies: SubAgencyARRow[], totals: SubAgencyARRow}, error?: string}>}
 */
export const getSubAgencyAR = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/sub-agencies/ar`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load sub-agency AR' };
    }

    return {
      success: result.success !== false,
      data: result.data,
    };
  } catch (error) {
    console.error('Get sub-agency AR error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Map vital metric status to CSS color class for text
 * @param {string} status - "normal" | "warning" | "critical"
 * @returns {string} Tailwind CSS color class
 */
export const getStatusTextClass = (status) => {
  switch (status) {
    case 'critical':
      return 'text-red-600 dark:text-red-400';
    case 'warning':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'normal':
    default:
      return 'text-gray-900 dark:text-white';
  }
};

/**
 * Map vital metric status to background color class
 * @param {string} status - "normal" | "warning" | "critical"
 * @returns {string} Tailwind CSS background color class
 */
export const getStatusBgClass = (status) => {
  switch (status) {
    case 'critical':
      return 'bg-red-50 dark:bg-red-900/30';
    case 'warning':
      return 'bg-yellow-50 dark:bg-yellow-900/30';
    case 'normal':
    default:
      return 'bg-gray-50 dark:bg-gray-700/50';
  }
};

/**
 * Map vital metric status to badge color classes
 * @param {string} status - "normal" | "warning" | "critical"
 * @returns {string} Tailwind CSS classes for badge styling
 */
export const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'critical':
      return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
    case 'warning':
      return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
    case 'normal':
    default:
      return 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light';
  }
};

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (MM/DD/YYYY)
 */
export const formatVitalsDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format a date string for display with time
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (MM/DD/YYYY h:mm AM/PM)
 */
export const formatVitalsDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format currency for display (fallback if displayValue not available)
 * @param {number} amount - Numeric amount
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Check if a proximity date is approaching (within specified days)
 * @param {string} proximityDate - ISO date string
 * @param {number} withinDays - Number of days to check (default 30)
 * @returns {boolean}
 */
export const isProximityDateApproaching = (proximityDate, withinDays = 30) => {
  if (!proximityDate) return false;
  try {
    const date = new Date(proximityDate);
    const now = new Date();
    const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return daysUntil >= 0 && daysUntil <= withinDays;
  } catch {
    return false;
  }
};

/**
 * Get days until a proximity date
 * @param {string} proximityDate - ISO date string
 * @returns {number|null} Days until date, or null if invalid
 */
export const getDaysUntilProximityDate = (proximityDate) => {
  if (!proximityDate) return null;
  try {
    const date = new Date(proximityDate);
    const now = new Date();
    return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};
